/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Button, useNotification, type ButtonProps } from '@az/starc-ui';
import { useReviewFormData } from '../../api/getReviewFormData';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type {
  SubmitReviewCloseOptions,
  SubmitReviewDefaultValues,
  SubmitReviewPageType,
} from '../../interface';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useLabels } from '@/hooks/useLabels';

const LazySubmitProductReviewModal = dynamic(
  () => import('../SubmitProductReviewModal').then((mod) => mod.SubmitProductReviewModal),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

export type SubmitReviewButtonProps = {
  productId: string | undefined;
  productImage: string | undefined;
  productTitle: string | undefined;
  label: NonNullable<React.ReactNode>;
  defaultValues?: SubmitReviewDefaultValues;
  startIcon?: ButtonProps['startIcon'];
  variant?: 'primary' | 'secondary' | 'pill-secondary';
  className?: string;
  pageType: SubmitReviewPageType;
  onClose?: VoidFunction;
  onSuccess?: VoidFunction;
  onDuplicated?: VoidFunction;
};

const labelMap = {
  errorNotificationTitle: 'label_reviews_submit_reviewErrorNotificationTitle',
  errorNotificationText: 'label_reviews_submit_errorNotificationText',
  successNotificationText: 'label_reviews_pendingReviewMessage',
};

export const SubmitReviewButton = forwardRef(function SubmitReviewButton(
  {
    productId,
    productImage,
    productTitle,
    label,
    startIcon,
    variant,
    defaultValues,
    className,
    pageType,
    onClose,
    onSuccess,
    onDuplicated,
  }: SubmitReviewButtonProps,
  ref
) {
  const labels = useLabels(labelMap);
  const [isSubmitReviewOpen, setIsSubmitReviewOpen] = useState(false);

  const writeReviewButtonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => writeReviewButtonRef.current);

  const notification = useNotification();

  const {
    data: formData,
    hasSubmitted,
    isPending,
    mutate: getReviewFormData,
  } = useReviewFormData({
    productId,
  });

  useEffect(() => {
    if (typeof hasSubmitted === 'boolean') {
      if (hasSubmitted) {
        onDuplicated?.();
        const id = notification.show({
          type: 'error',
          title: labels.errorNotificationTitle,
          text: labels.errorNotificationText,
          position: 'top-end',
          hasCloseButton: true,
          onClose: () => notification.hide(id),
        });
      } else {
        setIsSubmitReviewOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted]);

  const openSubmitReview = () => {
    getReviewFormData();
  };

  const closeSubmitReview = ({ status }: SubmitReviewCloseOptions) => {
    setIsSubmitReviewOpen(false);
    onClose?.();
    writeReviewButtonRef.current?.focus();

    if (status === 'success') {
      onSuccess?.();
      const id = notification.show({
        type: 'snack-bar',
        text: labels.successNotificationText,
        onClose: () => notification.hide(id),
      });
    }
  };

  return (
    <>
      <Button
        className={className}
        variant={variant ?? 'secondary'}
        onClick={openSubmitReview}
        ref={writeReviewButtonRef}
        loading={isPending}
        startIcon={startIcon}
        disabled={hasSubmitted}
      >
        {label}
      </Button>
      {isSubmitReviewOpen && formData && productId && productTitle ? (
        <LazySubmitProductReviewModal
          isOpen={isSubmitReviewOpen}
          onClose={closeSubmitReview}
          productId={productId}
          productImage={productImage}
          productTitle={productTitle}
          defaultValues={defaultValues}
          formData={formData}
          pageType={pageType}
        />
      ) : null}
    </>
  );
});
